import classNames from 'classnames';
import fp from 'lodash/fp';
import React from 'react';
import { graphql } from 'react-apollo';
import { Alert, Button, Col, Container, Row } from 'reactstrap';
import { branch, compose, pure, withHandlers, withProps, withStateHandlers } from 'recompose';
import { eventValueHandler, noCapMap } from '../../../../common';
import { routes } from '../../../../constants';
import { FormControlItem } from '../../../composed';
import { hideIfNoEventData, notFoundIfNoEventData, withState } from '../../../higher-order';
import { Icon } from '../../../proxy';
import eventMutation from './event-mutation.gql';
import eventQuery from './event-query.gql';
import './event.css';

const Event = ({
  event,
  eventUpdateHandler,
  updateEvent,
  submitted,
  showError,
  hideError,
  error,
  onSubmit
}) => (
    <section className='event flex-center'>
      <Container className='mt-5'>
        <form
          className={classNames('mb-4', { 'was-validated': submitted })}
          noValidate
          onSubmit={onSubmit}>
          <Alert
            color='danger'
            className='mb-5'
            isOpen={showError}
            toggle={hideError}>
            {error}
          </Alert>
          <FormControlItem
            id='name'
            onChange={eventUpdateHandler('name')}
            required
            value={event.name}>
            <div className='invalid-feedback ml-5'>
              Name is required.
            </div>
          </FormControlItem>
          <Row>
            <Col xs={12} sm={6}>
              <FormControlItem
                id='start-date'
                onChange={eventUpdateHandler('startDate')}
                required
                value={event.startDate}>
                <div className='invalid-feedback ml-5'>
                  Start Date is required.
                </div>
              </FormControlItem>
            </Col>
            <Col xs={12} sm={6}>
              <FormControlItem
                id='end-date'
                onChange={eventUpdateHandler('endDate')}
                value={event.endDate} />
            </Col>
          </Row>
          <Row>
            <Col xs={12} sm={6}>
              <FormControlItem
                id='continent'
                onChange={eventUpdateHandler('location.continent')}
                value={event.location.continent} />
            </Col>
            <Col xs={12} sm={6}>
              <FormControlItem
                id='country'
                onChange={eventUpdateHandler('location.country')}
                required
                value={event.location.country}>
                <div className='invalid-feedback ml-5'>
                  Country is required.
                </div>
              </FormControlItem>
            </Col>
          </Row>
          <Row>
            <Col xs={12} sm={6}>
              <FormControlItem
                id='state'
                onChange={eventUpdateHandler('location.state')}
                value={event.location.state} />
            </Col>
            <Col xs={12} sm={6}>
              <FormControlItem
                id='city'
                onChange={eventUpdateHandler('location.city')}
                value={event.location.city} />
            </Col>
          </Row>
          <FormControlItem
            id='image'
            onChange={eventUpdateHandler('image')}
            required
            value={event.image}>
            <div className='invalid-feedback ml-5'>
              Image is required.
            </div>
          </FormControlItem>
          <FormControlItem
            id='website'
            onChange={eventUpdateHandler('website')}
            value={event.website} />
          <Row>
            <Col xs={12} sm={6}>
              <FormControlItem
                id='facebook-profile'
                onChange={eventUpdateHandler('socialLinks.facebook.profile')}
                value={event.socialLinks.facebook.profile} />
            </Col>
            <Col xs={12} sm={6}>
              <FormControlItem
                id='facebook-page'
                onChange={eventUpdateHandler('socialLinks.facebook.page')}
                value={event.socialLinks.facebook.page} />
            </Col>
          </Row>
          <Row>
            <Col xs={12} sm={6}>
              <FormControlItem
                id='spotify'
                onChange={eventUpdateHandler('socialLinks.spotify')}
                value={event.socialLinks.spotify} />
            </Col>
            <Col xs={12} sm={6}>
              <FormControlItem
                id='instagram'
                onChange={eventUpdateHandler('socialLinks.instagram')}
                value={event.socialLinks.instagram} />
            </Col>
          </Row>
          <Row>
            <Col xs={12} sm={6}>
              <FormControlItem
                id='youtube'
                onChange={eventUpdateHandler('socialLinks.youtube')}
                value={event.socialLinks.youtube} />
            </Col>
            <Col xs={12} sm={6}>
              <FormControlItem
                id='twitter'
                onChange={eventUpdateHandler('socialLinks.twitter')}
                value={event.socialLinks.twitter} />
            </Col>
          </Row>
          <h6>Styles</h6>
          <Row>
            {
              noCapMap((style, index) =>
                <Col key={index} xs={12} sm={6}>
                  <FormControlItem
                    id={`style-${index}`}
                    className='remove'
                    label={null}
                    onChange={eventUpdateHandler(`styles[${index}]`)}
                    value={style}>
                    <Button
                      color='red'
                      className='btn-sm suffix'
                      onClick={() =>
                        updateEvent({
                          path: 'styles',
                          value: fp.pullAt([index])(event.styles)
                        })}>
                      <Icon icon='close' />
                    </Button>
                  </FormControlItem>
                </Col>
              )(event.styles)
            }
            {
              fp.last(event.styles) !== '' ?
                <Col xs={12} sm={6} className='flex-center'>
                  <Button
                    color='primary'
                    onClick={() =>
                      updateEvent({
                        path: 'styles',
                        value: [...event.styles, '']
                      })
                    }>
                    <Icon icon='plus' />
                  </Button>
                </Col> :
                null
            }
          </Row>
          <h6>Artists</h6>
          <Row>
            {
              noCapMap((artist, index) =>
                <Col key={index} xs={12} sm={6}>
                  <FormControlItem
                    id={`artist-${index}`}
                    className='remove'
                    label={null}
                    onChange={eventUpdateHandler(`artists[${index}]`)}
                    value={artist}>
                    <Button
                      color='red'
                      className='btn-sm suffix'
                      onClick={() =>
                        updateEvent({
                          path: 'artists',
                          value: fp.pullAt([index])(event.artists)
                        })}>
                      <Icon icon='close' />
                    </Button>
                  </FormControlItem>
                </Col>
              )(event.artists)
            }
            {
              fp.overSome([
                fp.isEmpty,
                fp.flow(
                  fp.last,
                  fp.negate(fp.isEmpty)
                )
              ])(event.artists) ?
                <Col xs={12} sm={6} className='flex-center'>
                  <Button
                    color='primary'
                    onClick={() =>
                      updateEvent({
                        path: 'artists',
                        value: [...event.artists, '']
                      })
                    }>
                    <Icon icon='plus' />
                  </Button>
                </Col> :
                null
            }
          </Row>
          <Row>
            <Col className='text-center'>
              <Button color='primary' type='submit'>
                Submit
              </Button>
            </Col>
          </Row>
        </form>
      </Container>
    </section>
  );

const omitEmptyFields = (obj) =>
  fp.flow(
    fp.mapValues((value) =>
      fp.isPlainObject(value) ?
        omitEmptyFields(value) :
        value
    ),
    fp.omitBy(
      fp.overEvery([
        fp.negate(fp.isBoolean),
        fp.isEmpty
      ])
    )
  )(obj);

const eventState = (event) => ({
  name: fp.getOr('')('name')(event),
  startDate: fp.getOr('')('startDate')(event),
  endDate: fp.getOr('')('endDate')(event),
  location: {
    continent: fp.getOr('')('location.continent')(event),
    country: fp.getOr('')('location.country')(event),
    state: fp.getOr('')('location.state')(event),
    city: fp.getOr('')('location.city')(event)
  },
  image: fp.getOr('')('image')(event),
  website: fp.getOr('')('website')(event),
  socialLinks: {
    facebook: {
      profile: fp.getOr('')('socialLinks.facebook.profile')(event),
      page: fp.getOr('')('socialLinks.facebook.page')(event)
    },
    spotify: fp.getOr('')('socialLinks.spotify')(event),
    instagram: fp.getOr('')('socialLinks.instagram')(event),
    youtube: fp.getOr('')('socialLinks.youtube')(event),
    twitter: fp.getOr('')('socialLinks.twitter')(event)
  },
  styles: fp.getOr(['zouk'])('styles')(event),
  artists: fp.flow(
    fp.getOr([])('artists'),
    fp.map(fp.get('name'))
  )(event)
});

const withEventState = withStateHandlers(({
  data: {
    event
  } = {}
}) => ({
  event: fp.flow(
    omitEmptyFields,
    eventState
  )(event)
}), {
    updateEvent: ({
      event
    }) => ({
      path,
      value
    }) => ({
      event: fp.set(path)(
        fp.isFunction(value) ?
          value(event) :
          value
      )({ ...event })
    })
  }
);

export default compose(
  branch(
    ({ match: { url } }) => url !== routes.adminPortal.createEvent,
    compose(
      withProps(({ match: { params: { id } } }) => ({
        id
      })),
      graphql(eventQuery),
      hideIfNoEventData('data'),
      notFoundIfNoEventData('data')
    )
  ),
  withEventState,
  graphql(eventMutation, {
    options: { errorPolicy: 'all' },
    props: ({ mutate }) => ({
      putEvent: (event) => {
        mutate({
          variables: {
            event: omitEmptyFields(event)
          }
        })
      }
    })
  }),
  withState({
    submitted: false,
    submitting: false,
    showError: false
  }),
  withHandlers({
    eventUpdateHandler: ({ updateEvent }) =>
      (path, transform) => eventValueHandler(
        (value) => updateEvent({
          path,
          value: transform ?
            transform(value) :
            value
        })
      ),
    onSubmit: ({
      event,
      setSubmitted,
      setSubmitting,
      putEvent
    }) =>
      (e) => {
        e.preventDefault();
        e.stopPropagation();

        setSubmitted(true);

        if (e.target.checkValidity()) {
          setSubmitting(true);
          putEvent(event);
        }
      },
    hideError: ({ setShowError }) => () => setShowError(false)
  }),
  pure
)(Event);



// WEBPACK FOOTER //
// ./src/components/pages/admin-portal/event/event.js